import { createApi } from '@reduxjs/toolkit/query/react';
import { DateTime } from 'luxon';
import {
  Dealer,
  DealerFilters,
  PaginatedResponse,
  PaginationParams,
  SortParams,
} from '../../types';
import { applyDirection, baseQueryStub, compareValues, logError } from '../../utils';
import { DealerService } from './dealer-service';

// Improve pagination logic to work with cached data
const paginateDealers = (
  dealers: Dealer[],
  pagination: PaginationParams,
  filters?: DealerFilters,
  sort?: SortParams
): PaginatedResponse<Dealer> => {
  // Apply filters if provided
  let filteredData = [...dealers];

  if (filters) {
    filteredData = filteredData.filter(dealer => {
      return Object.entries(filters).every(([key, value]) => {
        // Skip undefined, null, or empty string filters
        if (value === undefined || value === null || value === '') return true;

        const dealerValue = dealer[key as keyof Dealer];

        // Handle status specially since it's a boolean but might come as a string
        if (key === 'status' && typeof value === 'string') {
          return dealerValue === (value === 'true');
        }

        // Case-insensitive string search for string values
        if (typeof dealerValue === 'string' && typeof value === 'string') {
          return dealerValue.toLowerCase().includes(value.toLowerCase());
        }

        return dealerValue === value;
      });
    });
  }

  // Apply sorting if provided
  if (sort?.field && sort?.direction) {
    filteredData.sort((a, b) => {
      const fieldKey = sort.field as keyof Dealer;
      const valueA = a[fieldKey];
      const valueB = b[fieldKey];

      const result = compareValues(valueA, valueB);
      return applyDirection(result, sort.direction);
    });
  }

  // Apply pagination
  const { page = 1, pageSize = 10 } = pagination;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  return {
    data: paginatedData,
    totalCount: filteredData.length,
    page,
    pageSize,
    totalPages: Math.ceil(filteredData.length / pageSize),
  };
};

// A helper function to initialize the service once when needed
let dealerService: DealerService | null = null;
const getDealerService = () => {
  dealerService ??= new DealerService();
  return dealerService;
};

// Cache for all dealers
let allDealersCache: {
  data: Dealer[] | null;
  timestamp: number;
  expiryTime: number;
} = {
  data: null,
  timestamp: 0,
  expiryTime: 5 * 60 * 1000, // 5 minutes in milliseconds
};

// Function to get all dealers with caching
const getAllDealersWithCache = async (): Promise<Dealer[]> => {
  const currentTime = DateTime.now().toMillis();

  // If cache is valid, return cached data
  if (
    allDealersCache.data !== null &&
    currentTime - allDealersCache.timestamp < allDealersCache.expiryTime
  ) {
    return allDealersCache.data;
  }

  // Cache is invalid or expired, fetch fresh data
  try {
    const dealerService = getDealerService();
    const dealers = await dealerService.getAllDealers();

    // Update cache
    allDealersCache = {
      data: dealers,
      timestamp: currentTime,
      expiryTime: allDealersCache.expiryTime,
    };

    return dealers;
  } catch (error) {
    // If there's an error but we have cached data, return it even if expired
    if (allDealersCache.data !== null) {
      logError('Error fetching fresh dealers data, using expired cache:', error);
      return allDealersCache.data;
    }

    // No cached data available, must throw error
    throw error;
  }
};

// Function to invalidate the cache
const invalidateDealersCache = () => {
  allDealersCache.data = null;
  allDealersCache.timestamp = 0;
};

// Define an API service using RTK Query that uses the DealerService
export const dealersApi = createApi({
  reducerPath: 'dealersApi',
  baseQuery: baseQueryStub,
  tagTypes: ['Dealer', 'DocumentTiers'],
  endpoints: builder => ({
    getDealers: builder.query<
      PaginatedResponse<Dealer>,
      {
        pagination: PaginationParams;
        filters?: DealerFilters;
        sort?: SortParams;
      }
    >({
      queryFn: async ({ pagination, filters, sort }) => {
        // Use the cached version of getAllDealers
        const allDealers = await getAllDealersWithCache();

        // Apply client-side pagination, filtering, and sorting
        const paginatedResponse = paginateDealers(allDealers, pagination, filters, sort);

        return { data: paginatedResponse };
      },
      providesTags: result =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Dealer' as const, id })),
              { type: 'Dealer', id: 'LIST' },
            ]
          : [{ type: 'Dealer', id: 'LIST' }],
    }),
    getAllDealers: builder.query<Dealer[], void>({
      queryFn: async () => {
        // Use the cached version
        const dealers = await getAllDealersWithCache();
        return { data: dealers };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Dealer' as const, id })),
              { type: 'Dealer', id: 'LIST' },
            ]
          : [{ type: 'Dealer', id: 'LIST' }],
    }),
    getDealersByManufacturerId: builder.query<Dealer[], number>({
      queryFn: async manufacturerId => {
        const dealerService = getDealerService();
        const dealers = await dealerService.getDealersByManufacturerId(manufacturerId);
        return { data: dealers };
      },
      providesTags: ['Dealer'],
    }),
    getActiveDealersByManufacturerId: builder.query<Dealer[], number>({
      queryFn: async manufacturerId => {
        const dealerService = getDealerService();
        const dealers = await dealerService.getDealersByManufacturerId(manufacturerId);

        // Filter for active dealers and sort alphabetically by name
        const activeDealers = dealers
          .filter(dealer => dealer.status)
          .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

        return { data: activeDealers };
      },
      providesTags: ['Dealer'],
    }),
    getDealerById: builder.query<Dealer, string>({
      queryFn: async id => {
        // Don't go against cache. Get latest version.
        const dealerService = getDealerService();
        const dealer = await dealerService.getDealerById(id);
        return { data: dealer };
      },
      providesTags: (_, __, id) => [{ type: 'Dealer', id }],
    }),
    addDealer: builder.mutation<Dealer, { dealerData: Omit<Dealer, 'id'> }>({
      queryFn: async ({ dealerData }) => {
        const dealerService = getDealerService();
        const newDealer = await dealerService.createDealer(dealerData);

        // Invalidate cache
        invalidateDealersCache();

        return { data: newDealer };
      },
      invalidatesTags: [{ type: 'Dealer', id: 'LIST' }],
    }),
    updateDealer: builder.mutation<Dealer, { id: string; dealerData: Partial<Dealer> }>({
      queryFn: async ({ id, dealerData }) => {
        const dealerService = getDealerService();
        const updatedDealer = await dealerService.updateDealer(id, dealerData);

        // Invalidate cache
        invalidateDealersCache();

        return { data: updatedDealer };
      },
      invalidatesTags: (_, __, { id }) => [
        { type: 'Dealer', id },
        { type: 'Dealer', id: 'LIST' },
      ],
    }),
    deleteDealer: builder.mutation<void, string>({
      queryFn: async id => {
        const dealerService = getDealerService();
        await dealerService.deleteDealer(id);

        // Invalidate cache
        invalidateDealersCache();

        return { data: undefined };
      },
      invalidatesTags: (_, __, id) => [
        { type: 'Dealer', id },
        { type: 'Dealer', id: 'LIST' },
      ],
    }),
    getDocumentTiers: builder.query<string[], void>({
      queryFn: async () => {
        const dealerService = getDealerService();
        const tiers = await dealerService.getDocumentTiers();
        return { data: tiers };
      },
      keepUnusedDataFor: 3600 * 24, // 24 hours
      providesTags: ['DocumentTiers'],
    }),
  }),
});

export const {
  useGetDealersQuery,
  useGetAllDealersQuery,
  useGetDealerByIdQuery,
  useGetDealersByManufacturerIdQuery,
  useGetActiveDealersByManufacturerIdQuery,
  useAddDealerMutation,
  useUpdateDealerMutation,
  useDeleteDealerMutation,
  useGetDocumentTiersQuery,
  usePrefetch,
} = dealersApi;
